import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ShieldCheck,
  ArrowLeft,
  CaretUp,
  CaretDown,
  Minus,
  Warning,
  CheckCircle,
  XCircle,
  Eye,
  Info,
  ArrowClockwise,
  Bell,
  CheckSquare,
} from '@phosphor-icons/react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, ReferenceLine,
} from 'recharts';
import api from '../lib/api';
import DayOverridesPanel from './DayOverridesPanel';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const READINESS_CONFIG = {
  READY: { label: 'Ready', color: 'emerald', bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/30', ring: '#10b981' },
  SHADOW_OK: { label: 'Shadow OK', color: 'blue', bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/30', ring: '#3b82f6' },
  REVIEW_REQUIRED: { label: 'Review', color: 'amber', bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', ring: '#f59e0b' },
  BLOCKED: { label: 'Blocked', color: 'red', bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/30', ring: '#ef4444' },
  INSUFFICIENT_DATA: { label: 'No Signal', color: 'zinc', bg: 'bg-zinc-500/10', text: 'text-zinc-400', border: 'border-zinc-500/30', ring: '#71717a' },
};

const ACTION_CONFIG = {
  READY_FOR_MANUAL_CUTOVER: { label: 'Ready for Manual Cutover', icon: CheckCircle, color: 'text-emerald-400' },
  CONTINUE_SHADOW_MODE: { label: 'Continue Shadow Mode', icon: Eye, color: 'text-blue-400' },
  NEEDS_INVESTIGATION: { label: 'Needs Investigation', icon: Warning, color: 'text-amber-400' },
  BLOCK_CUTOVER: { label: 'Block Cutover', icon: XCircle, color: 'text-red-400' },
  KEEP_UPLOAD_CANONICAL: { label: 'Keep Upload as Canonical', icon: Info, color: 'text-zinc-400' },
};

const VARIANCE_STATUS_CONFIG = {
  MATCHED: { label: 'Matched', color: '#10b981' },
  MINOR_VARIANCE: { label: 'Minor', color: '#3b82f6' },
  REVIEW: { label: 'Review', color: '#f59e0b' },
  BLOCKED: { label: 'Blocked', color: '#ef4444' },
};

const SCORE_COMPONENT_CONFIG = {
  variance_score: { label: 'Variance Quality', max: 30, color: '#06b6d4' },
  completeness_score: { label: 'Data Completeness', max: 25, color: '#8b5cf6' },
  sync_health_score: { label: 'Sync Health', max: 20, color: '#10b981' },
  tax_consistency_score: { label: 'Tax Consistency', max: 10, color: '#f59e0b' },
  stability_score: { label: 'Stability', max: 10, color: '#3b82f6' },
  structural_integrity_score: { label: 'Structural Integrity', max: 5, color: '#ec4899' },
};

const ALERT_TYPE_CONFIG = {
  STATE_DOWNGRADE: { label: 'State Downgrade', icon: CaretDown, color: 'text-red-400' },
  STATE_UPGRADE: { label: 'State Upgrade', icon: CaretUp, color: 'text-emerald-400' },
  BLOCKER_APPEARED: { label: 'Blocker Appeared', icon: XCircle, color: 'text-amber-400' },
  BLOCKER_RESOLVED: { label: 'Blocker Resolved', icon: CheckCircle, color: 'text-emerald-400' },
  CONFIDENCE_DROP_SPIKE: { label: 'Confidence Drop', icon: CaretDown, color: 'text-red-400' },
  CONFIDENCE_RECOVERY: { label: 'Confidence Recovery', icon: CaretUp, color: 'text-emerald-400' },
};

const ALERT_SEVERITY_STYLES = {
  CRITICAL: { bg: 'bg-red-500/5', border: 'border-red-500/20', text: 'text-red-400', dot: 'bg-red-500' },
  HIGH: { bg: 'bg-amber-500/5', border: 'border-amber-500/20', text: 'text-amber-400', dot: 'bg-amber-500' },
  MEDIUM: { bg: 'bg-blue-500/5', border: 'border-blue-500/20', text: 'text-blue-400', dot: 'bg-blue-500' },
  LOW: { bg: 'bg-zinc-500/5', border: 'border-zinc-500/20', text: 'text-zinc-400', dot: 'bg-zinc-500' },
  INFO: { bg: 'bg-emerald-500/5', border: 'border-emerald-500/20', text: 'text-emerald-400', dot: 'bg-emerald-500' },
};

// ---------------------------------------------------------------------------
// Utility components
// ---------------------------------------------------------------------------

const ScoreRing = ({ score, size = 80, strokeWidth = 6, readiness }) => {
  const cfg = READINESS_CONFIG[readiness] || READINESS_CONFIG.INSUFFICIENT_DATA;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(score, 100) / 100;
  const offset = circumference * (1 - pct);

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke="rgba(255,255,255,0.05)" strokeWidth={strokeWidth} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={cfg.ring} strokeWidth={strokeWidth}
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" className="transition-all duration-700" />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-lg font-mono font-medium text-white">{Math.round(score)}</span>
      </div>
    </div>
  );
};

const ReadinessBadge = ({ state, size = 'sm' }) => {
  const cfg = READINESS_CONFIG[state] || READINESS_CONFIG.INSUFFICIENT_DATA;
  const cls = size === 'lg'
    ? `px-3 py-1.5 text-sm font-medium rounded-md ${cfg.bg} ${cfg.text} border ${cfg.border}`
    : `px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider rounded ${cfg.bg} ${cfg.text} border ${cfg.border}`;
  return <span className={cls} data-testid={`readiness-badge-${state}`}>{cfg.label}</span>;
};

const TrendIndicator = ({ trend }) => {
  if (trend === 'improving') return <span className="flex items-center gap-0.5 text-emerald-400 text-xs"><CaretUp size={12} weight="fill" /> Improving</span>;
  if (trend === 'degrading') return <span className="flex items-center gap-0.5 text-red-400 text-xs"><CaretDown size={12} weight="fill" /> Degrading</span>;
  return <span className="flex items-center gap-0.5 text-zinc-500 text-xs"><Minus size={12} /> Stable</span>;
};

const BlockerTag = ({ blocker }) => {
  const isCritical = blocker.severity === 'critical';
  return (
    <div className={`flex items-start gap-2 px-3 py-2 rounded-md text-xs ${
      isCritical ? 'bg-red-500/5 border border-red-500/20 text-red-400' : 'bg-amber-500/5 border border-amber-500/20 text-amber-400'
    }`} data-testid={`blocker-${blocker.code}`}>
      {isCritical ? <XCircle size={14} weight="fill" className="mt-0.5 shrink-0" /> : <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />}
      <div>
        <span className="font-mono">{blocker.code}</span>
        <p className="text-zinc-500 mt-0.5">{blocker.detail}</p>
      </div>
    </div>
  );
};

const ComponentBar = ({ name, value, config }) => {
  const pct = config.max > 0 ? (value / config.max) * 100 : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-zinc-400">{config.label}</span>
        <span className="font-mono text-zinc-300">{value}/{config.max}</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, backgroundColor: config.color }} />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Overview card
// ---------------------------------------------------------------------------

const OutletCard = ({ outlet, onClick }) => {
  const cfg = READINESS_CONFIG[outlet.readiness_state] || READINESS_CONFIG.INSUFFICIENT_DATA;
  const actionCfg = ACTION_CONFIG[outlet.recommended_action] || ACTION_CONFIG.KEEP_UPLOAD_CANONICAL;
  const ActionIcon = actionCfg.icon;

  return (
    <div
      onClick={onClick}
      className="surface-base rounded-lg p-5 cursor-pointer card-hover border border-transparent hover:border-white/10 transition-all duration-200"
      data-testid={`outlet-card-${outlet.outlet_id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0 pr-3">
          <p className="text-xs font-mono text-zinc-500 truncate">{outlet.outlet_id}</p>
          <div className="flex items-center gap-2 mt-1">
            <ReadinessBadge state={outlet.readiness_state} />
            <TrendIndicator trend={outlet.trend} />
          </div>
        </div>
        <ScoreRing score={outlet.confidence_score} size={64} strokeWidth={5} readiness={outlet.readiness_state} />
      </div>

      {/* Details grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <div>
          <span className="text-zinc-500">Source Mode</span>
          <p className="text-zinc-300 font-mono">{outlet.source_mode}</p>
        </div>
        <div>
          <span className="text-zinc-500">Stable Days</span>
          <p className="text-zinc-300 font-mono">{outlet.consecutive_stable_days}</p>
        </div>
        <div>
          <span className="text-zinc-500">Comparisons</span>
          <p className="text-zinc-300 font-mono">{outlet.comparison_day_count} day(s)</p>
        </div>
        <div>
          <span className="text-zinc-500">Last Variance</span>
          <p className="text-zinc-300 font-mono">{outlet.last_comparison_date || '—'}</p>
        </div>
      </div>

      {/* Recommendation */}
      <div className={`mt-3 pt-3 border-t border-white/5 flex items-center gap-2 text-xs ${actionCfg.color}`}>
        <ActionIcon size={14} weight="fill" />
        <span>{actionCfg.label}</span>
      </div>

      {/* Top blocker */}
      {outlet.top_blocker && (
        <div className={`mt-2 px-2 py-1 rounded text-[10px] font-mono ${
          outlet.top_blocker.severity === 'critical' ? 'bg-red-500/5 text-red-400/70' : 'bg-amber-500/5 text-amber-400/70'
        }`}>
          {outlet.top_blocker.code}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Detail view
// ---------------------------------------------------------------------------

const OutletDetailView = ({ outletId, onBack }) => {
  const [detail, setDetail] = useState(null);
  const [history, setHistory] = useState([]);
  const [outletAlerts, setOutletAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [detailRes, historyRes, alertsRes] = await Promise.all([
        api.get(`/source-confidence/${outletId}`),
        api.get(`/source-confidence/${outletId}/history?limit=30`),
        api.get(`/source-confidence/alerts?outlet_id=${outletId}&limit=20`),
      ]);
      setDetail(detailRes.data);
      setHistory(historyRes.data.history || []);
      setOutletAlerts(alertsRes.data.alerts || []);
    } catch (err) {
      toast.error('Failed to load outlet confidence data');
    } finally {
      setLoading(false);
    }
  }, [outletId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Loading confidence data...</div>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="text-center py-12">
        <p className="text-zinc-400">No data found for this outlet.</p>
        <button onClick={onBack} className="mt-4 text-cyan-400 text-sm hover:underline">Go back</button>
      </div>
    );
  }

  const cfg = READINESS_CONFIG[detail.readiness_state] || READINESS_CONFIG.INSUFFICIENT_DATA;
  const actionCfg = ACTION_CONFIG[detail.recommended_action] || ACTION_CONFIG.KEEP_UPLOAD_CANONICAL;
  const ActionIcon = actionCfg.icon;

  const chartData = [...history].reverse().map(h => ({
    date: h.business_date,
    score: h.day_confidence_score,
    sales_var: h.net_sales_variance_pct,
    order_var: h.order_count_variance_pct,
    tax_var: h.tax_variance_pct,
    status: h.variance_status,
  }));

  const tabs = [
    { id: 'overview', label: 'Score Breakdown' },
    { id: 'history', label: 'Variance Timeline' },
    { id: 'blockers', label: `Blockers (${detail.blockers.length})` },
    { id: 'alerts', label: `Alerts (${outletAlerts.length})` },
  ];

  return (
    <div className="space-y-6" data-testid="outlet-detail-view">
      {/* Back + Header */}
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-2 rounded-md hover:bg-white/5 text-zinc-400 hover:text-white transition-colors" data-testid="back-btn">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <p className="text-xs font-mono text-zinc-500">{detail.outlet_id}</p>
          <div className="flex items-center gap-3 mt-1">
            <ReadinessBadge state={detail.readiness_state} size="lg" />
            <TrendIndicator trend={detail.trend} />
          </div>
        </div>
        <ScoreRing score={detail.confidence_score} size={96} strokeWidth={7} readiness={detail.readiness_state} />
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {[
          { label: 'Score', value: Math.round(detail.confidence_score), mono: true },
          { label: 'Source Mode', value: detail.source_mode, mono: true },
          { label: 'Stable Days', value: detail.consecutive_stable_days, mono: true },
          { label: 'Comparisons', value: detail.comparison_day_count, mono: true },
          { label: 'POS Data', value: detail.has_pos_data ? 'Yes' : 'No', mono: true },
          { label: 'Last Variance', value: detail.last_variance_status || '—', mono: true },
        ].map(s => (
          <div key={s.label} className="bg-white/[0.02] rounded-md px-3 py-2">
            <p className="text-[10px] uppercase tracking-widest text-zinc-500">{s.label}</p>
            <p className={`text-sm mt-0.5 text-zinc-200 ${s.mono ? 'font-mono' : ''}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Recommendation card */}
      <div className={`surface-base rounded-lg p-4 border ${cfg.border}`}>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-md ${cfg.bg}`}>
            <ActionIcon size={20} weight="duotone" className={actionCfg.color} />
          </div>
          <div>
            <p className="text-xs text-zinc-500">Recommended Action</p>
            <p className={`text-sm font-medium ${actionCfg.color}`}>{actionCfg.label}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-white/5 pb-px">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 text-xs font-medium transition-colors rounded-t-md ${
              activeTab === t.id ? 'text-cyan-400 bg-cyan-500/5 border-b-2 border-cyan-400' : 'text-zinc-500 hover:text-zinc-300'
            }`}
            data-testid={`tab-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Score components */}
          <div className="surface-base rounded-lg p-5">
            <h4 className="text-xs uppercase tracking-widest text-zinc-500 mb-4">Score Components</h4>
            <div className="space-y-3">
              {Object.entries(SCORE_COMPONENT_CONFIG).map(([key, config]) => (
                <ComponentBar key={key} name={key} value={detail.components[key] || 0} config={config} />
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-white/5 flex justify-between text-xs">
              <span className="text-zinc-500">Raw Total</span>
              <span className="font-mono text-zinc-300">
                {Object.values(detail.components).reduce((a, b) => a + b, 0).toFixed(1)} / 100
              </span>
            </div>
          </div>

          {/* Penalties & Caps */}
          <div className="space-y-4">
            <div className="surface-base rounded-lg p-5">
              <h4 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Penalties Applied</h4>
              {Object.keys(detail.penalties).length === 0 ? (
                <p className="text-xs text-zinc-500">No penalties</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(detail.penalties).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-xs">
                      <span className="text-zinc-400 font-mono">{k}</span>
                      <span className="text-red-400 font-mono">{v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="surface-base rounded-lg p-5">
              <h4 className="text-xs uppercase tracking-widest text-zinc-500 mb-3">Score Caps</h4>
              {Object.keys(detail.caps).length === 0 ? (
                <p className="text-xs text-zinc-500">No caps applied</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(detail.caps).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-xs">
                      <span className="text-zinc-400 font-mono">{k}</span>
                      <span className="text-amber-400 font-mono">max {v}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-6">
          {/* Confidence trend chart */}
          {chartData.length > 0 ? (
            <>
              <div className="surface-base rounded-lg p-5">
                <h4 className="text-xs uppercase tracking-widest text-zinc-500 mb-4">Daily Confidence Score</h4>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                      <XAxis dataKey="date" stroke="#71717A" fontSize={10} />
                      <YAxis stroke="#71717A" fontSize={10} domain={[0, 100]} />
                      <Tooltip contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '11px' }}
                        labelStyle={{ color: '#a1a1aa' }} />
                      <ReferenceLine y={90} stroke="#10b981" strokeDasharray="4 4" strokeOpacity={0.3} />
                      <ReferenceLine y={70} stroke="#3b82f6" strokeDasharray="4 4" strokeOpacity={0.3} />
                      <ReferenceLine y={40} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.3} />
                      <Line type="monotone" dataKey="score" stroke="#06b6d4" strokeWidth={2} dot={false} name="Confidence" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Variance breakdown chart */}
              <div className="surface-base rounded-lg p-5">
                <h4 className="text-xs uppercase tracking-widest text-zinc-500 mb-4">Variance % by Day</h4>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                      <XAxis dataKey="date" stroke="#71717A" fontSize={10} />
                      <YAxis stroke="#71717A" fontSize={10} />
                      <Tooltip contentStyle={{ backgroundColor: '#18181B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', fontSize: '11px' }}
                        labelStyle={{ color: '#a1a1aa' }} />
                      <Bar dataKey="sales_var" name="Net Sales %" radius={[2, 2, 0, 0]}>
                        {chartData.map((entry, idx) => (
                          <Cell key={idx} fill={VARIANCE_STATUS_CONFIG[entry.status]?.color || '#71717a'} fillOpacity={0.7} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Daily table */}
              <div className="surface-base rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-white/5">
                      <th className="text-left px-4 py-3 text-zinc-500 font-medium">Date</th>
                      <th className="text-left px-4 py-3 text-zinc-500 font-medium">Status</th>
                      <th className="text-right px-4 py-3 text-zinc-500 font-medium">Score</th>
                      <th className="text-right px-4 py-3 text-zinc-500 font-medium">Sales Var %</th>
                      <th className="text-right px-4 py-3 text-zinc-500 font-medium">Order Var %</th>
                      <th className="text-right px-4 py-3 text-zinc-500 font-medium">Tax Var %</th>
                      <th className="text-left px-4 py-3 text-zinc-500 font-medium">Hint</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => {
                      const statusCfg = VARIANCE_STATUS_CONFIG[h.variance_status] || {};
                      return (
                        <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                          <td className="px-4 py-2.5 font-mono text-zinc-300">{h.business_date}</td>
                          <td className="px-4 py-2.5">
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ backgroundColor: `${statusCfg.color}15`, color: statusCfg.color }}>
                              {statusCfg.label || h.variance_status}
                            </span>
                          </td>
                          <td className="px-4 py-2.5 text-right font-mono text-zinc-300">{h.day_confidence_score}</td>
                          <td className="px-4 py-2.5 text-right font-mono text-zinc-300">{h.net_sales_variance_pct}%</td>
                          <td className="px-4 py-2.5 text-right font-mono text-zinc-300">{h.order_count_variance_pct}%</td>
                          <td className="px-4 py-2.5 text-right font-mono text-zinc-300">{h.tax_variance_pct}%</td>
                          <td className="px-4 py-2.5 text-zinc-500">{h.decision_hint}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="surface-base rounded-lg p-12 text-center">
              <p className="text-zinc-500 text-sm">No variance comparison history available yet.</p>
              <p className="text-zinc-600 text-xs mt-1">Variance data will appear here once POS vs Upload comparisons are computed.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'blockers' && (
        <div className="space-y-3">
          {detail.blockers.length === 0 ? (
            <div className="surface-base rounded-lg p-12 text-center">
              <CheckCircle size={32} weight="duotone" className="mx-auto text-emerald-400 mb-3" />
              <p className="text-zinc-300 text-sm">No blockers detected</p>
              <p className="text-zinc-500 text-xs mt-1">All confidence gates are passing.</p>
            </div>
          ) : (
            detail.blockers.map((b, i) => <BlockerTag key={i} blocker={b} />)
          )}
        </div>
      )}

      {activeTab === 'alerts' && (
        <div className="space-y-3">
          {outletAlerts.length === 0 ? (
            <div className="surface-base rounded-lg p-12 text-center">
              <Bell size={32} weight="duotone" className="mx-auto text-zinc-600 mb-3" />
              <p className="text-zinc-400 text-sm">No alerts for this outlet</p>
              <p className="text-zinc-600 text-xs mt-1">Alerts appear when confidence states change or critical blockers are detected.</p>
            </div>
          ) : (
            outletAlerts.map(a => <AlertCard key={a.alert_id} alert={a} />)
          )}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Recommendations view
// ---------------------------------------------------------------------------

const RecommendationsView = ({ recommendations }) => {
  if (!recommendations) return null;
  const groups = [
    { key: 'READY_FOR_MANUAL_CUTOVER', label: 'Ready for Manual Cutover', icon: CheckCircle, color: 'text-emerald-400', border: 'border-emerald-500/20' },
    { key: 'CONTINUE_SHADOW_MODE', label: 'Continue Shadow Mode', icon: Eye, color: 'text-blue-400', border: 'border-blue-500/20' },
    { key: 'NEEDS_INVESTIGATION', label: 'Needs Investigation', icon: Warning, color: 'text-amber-400', border: 'border-amber-500/20' },
    { key: 'BLOCK_CUTOVER', label: 'Block Cutover', icon: XCircle, color: 'text-red-400', border: 'border-red-500/20' },
    { key: 'KEEP_UPLOAD_CANONICAL', label: 'Keep Upload Canonical', icon: Info, color: 'text-zinc-400', border: 'border-zinc-500/20' },
  ];

  return (
    <div className="space-y-4" data-testid="recommendations-view">
      {groups.map(g => {
        const outlets = recommendations[g.key] || [];
        if (outlets.length === 0) return null;
        const Icon = g.icon;
        return (
          <div key={g.key} className={`surface-base rounded-lg border ${g.border}`}>
            <div className="flex items-center gap-2 px-4 py-3 border-b border-white/5">
              <Icon size={16} weight="fill" className={g.color} />
              <span className={`text-sm font-medium ${g.color}`}>{g.label}</span>
              <span className="text-xs text-zinc-500 font-mono ml-auto">{outlets.length}</span>
            </div>
            <div className="divide-y divide-white/[0.03]">
              {outlets.map(o => (
                <div key={o.outlet_id} className="flex items-center justify-between px-4 py-2.5 text-xs">
                  <span className="font-mono text-zinc-300">{o.outlet_id}</span>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-zinc-400">Score: {Math.round(o.confidence_score)}</span>
                    <ReadinessBadge state={o.readiness_state} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Alerts panel
// ---------------------------------------------------------------------------

const AlertCard = ({ alert, onAcknowledge }) => {
  const typeCfg = ALERT_TYPE_CONFIG[alert.alert_type] || { label: alert.alert_type, icon: Warning, color: 'text-zinc-400' };
  const sevCfg = ALERT_SEVERITY_STYLES[alert.severity] || ALERT_SEVERITY_STYLES.LOW;
  const TypeIcon = typeCfg.icon;
  const ts = alert.triggered_at ? new Date(alert.triggered_at).toLocaleString() : '';

  return (
    <div className={`rounded-lg border p-4 ${sevCfg.bg} ${sevCfg.border} ${alert.acknowledged ? 'opacity-50' : ''}`} data-testid={`alert-card-${alert.alert_id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${sevCfg.dot}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <TypeIcon size={14} weight="fill" className={typeCfg.color} />
            <span className={`text-xs font-medium ${typeCfg.color}`}>{typeCfg.label}</span>
            <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${sevCfg.bg} ${sevCfg.text}`}>{alert.severity}</span>
            {alert.acknowledged && <span className="text-[10px] text-zinc-500">Acknowledged</span>}
          </div>
          <p className="text-xs text-zinc-300 leading-relaxed">{alert.message}</p>
          <div className="flex items-center gap-3 mt-2">
            <span className="text-[10px] text-zinc-500 font-mono">{ts}</span>
            <span className="text-[10px] text-zinc-600 font-mono truncate">{alert.outlet_id}</span>
          </div>
          {alert.blockers && alert.blockers.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {alert.blockers.map((b, i) => (
                <span key={i} className="text-[9px] font-mono px-1.5 py-0.5 bg-white/[0.03] rounded text-zinc-500">{b}</span>
              ))}
            </div>
          )}
        </div>
        {!alert.acknowledged && onAcknowledge && (
          <button onClick={() => onAcknowledge(alert.alert_id)}
            className="shrink-0 p-1.5 rounded-md hover:bg-white/5 text-zinc-500 hover:text-white transition-colors"
            title="Acknowledge" data-testid={`ack-alert-${alert.alert_id}`}>
            <CheckSquare size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

const AlertsPanel = ({ onBack }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [showAcknowledged, setShowAcknowledged] = useState(false);

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSeverity !== 'all') params.append('severity', filterSeverity);
      if (!showAcknowledged) params.append('acknowledged', 'false');
      params.append('limit', '100');
      const res = await api.get(`/source-confidence/alerts?${params}`);
      setAlerts(res.data.alerts || []);
    } catch {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, [filterSeverity, showAcknowledged]);

  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleAcknowledge = async (alertId) => {
    try {
      await api.post(`/source-confidence/alerts/${alertId}/acknowledge`);
      setAlerts(prev => prev.map(a => a.alert_id === alertId ? { ...a, acknowledged: true } : a));
      toast.success('Alert acknowledged');
    } catch {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleAcknowledgeAll = async () => {
    try {
      const res = await api.post('/source-confidence/alerts/acknowledge-all');
      toast.success(`${res.data.acknowledged_count} alert(s) acknowledged`);
      fetchAlerts();
    } catch {
      toast.error('Failed to acknowledge alerts');
    }
  };

  const unackedCount = alerts.filter(a => !a.acknowledged).length;

  return (
    <div className="space-y-6" data-testid="alerts-panel">
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-2 rounded-md hover:bg-white/5 text-zinc-400 hover:text-white transition-colors" data-testid="back-from-alerts-btn">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <Bell size={20} weight="duotone" className="text-cyan-400" />
            <h2 className="text-lg font-medium font-['Outfit']">Confidence Alerts</h2>
          </div>
          <p className="text-xs text-zinc-500 mt-0.5">State transitions and critical changes</p>
        </div>
        {unackedCount > 0 && (
          <button onClick={handleAcknowledgeAll}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-zinc-400 bg-white/5 hover:bg-white/10 transition-colors"
            data-testid="acknowledge-all-btn"
          >
            Acknowledge All ({unackedCount})
          </button>
        )}
      </div>

      {/* Severity filter */}
      <div className="flex flex-wrap items-center gap-2">
        {['all', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'].map(s => {
          const sty = s === 'all' ? {} : ALERT_SEVERITY_STYLES[s];
          return (
            <button key={s} onClick={() => setFilterSeverity(s)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filterSeverity === s
                  ? s === 'all' ? 'bg-white/10 text-white' : `${sty.bg} ${sty.text} border ${sty.border}`
                  : 'bg-white/[0.03] text-zinc-500 hover:text-zinc-300'
              }`} data-testid={`alert-filter-${s}`}>
              {s === 'all' ? 'All' : s}
            </button>
          );
        })}
        <label className="flex items-center gap-1.5 text-xs text-zinc-500 ml-auto cursor-pointer">
          <input type="checkbox" checked={showAcknowledged} onChange={e => setShowAcknowledged(e.target.checked)}
            className="rounded border-zinc-600" data-testid="show-acknowledged-toggle" />
          Show acknowledged
        </label>
      </div>

      {/* Alert list */}
      {loading ? (
        <div className="flex items-center justify-center h-32"><div className="text-zinc-400 text-sm">Loading alerts...</div></div>
      ) : alerts.length === 0 ? (
        <div className="surface-base rounded-lg p-16 text-center">
          <Bell size={36} weight="duotone" className="mx-auto text-zinc-600 mb-3" />
          <p className="text-zinc-400 text-sm">No alerts</p>
          <p className="text-zinc-600 text-xs mt-1">Alerts appear when confidence states change or critical blockers are detected.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map(a => <AlertCard key={a.alert_id} alert={a} onAcknowledge={handleAcknowledge} />)}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const SEVERITY_PILL = {
  HIGH: 'bg-red-500/10 border-red-500/30 text-red-300',
  MEDIUM: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
  LOW: 'bg-zinc-500/10 border-zinc-500/30 text-zinc-400',
};

const ConflictsStrip = ({ initialSeverity = 'all' }) => {
  const [conflicts, setConflicts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [expanded, setExpanded] = useState(initialSeverity !== 'all');
  const [severity, setSeverity] = useState(initialSeverity);
  const [loading, setLoading] = useState(true);

  const fetchConflicts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/source-conflicts');
      setConflicts(res.data.conflicts || []);
      setSummary(res.data.summary || null);
    } catch {
      // Silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchConflicts(); }, [fetchConflicts]);

  const filtered = useMemo(() => {
    if (severity === 'all') return conflicts;
    return conflicts.filter(c => c.severity === severity);
  }, [conflicts, severity]);

  if (loading || !summary || summary.total === 0) return null;

  return (
    <div
      data-testid="conflicts-strip"
      className="surface-base rounded-lg border border-amber-500/20 bg-amber-500/[0.03]"
    >
      <button
        onClick={() => setExpanded(x => !x)}
        data-testid="conflicts-strip-toggle"
        className="w-full flex items-center gap-3 px-4 py-3 text-left"
      >
        <Warning size={18} weight="duotone" className="text-amber-400" />
        <div className="flex-1">
          <div className="text-sm font-medium text-amber-100">
            {summary.total} source conflict{summary.total === 1 ? '' : 's'} detected
          </div>
          <div className="text-[11px] text-zinc-400 mt-0.5">
            Sales Capture has shadowed lower-precedence sources on these slices. Read-only from <span className="font-mono">source_status</span>.
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          {summary.high > 0 && (
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md border ${SEVERITY_PILL.HIGH}`} data-testid="conflicts-summary-high">
              {summary.high} HIGH
            </span>
          )}
          {summary.medium > 0 && (
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md border ${SEVERITY_PILL.MEDIUM}`} data-testid="conflicts-summary-medium">
              {summary.medium} MED
            </span>
          )}
          {summary.low > 0 && (
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md border ${SEVERITY_PILL.LOW}`} data-testid="conflicts-summary-low">
              {summary.low} LOW
            </span>
          )}
          {expanded ? <CaretUp size={14} className="text-zinc-500 ml-1" /> : <CaretDown size={14} className="text-zinc-500 ml-1" />}
        </div>
      </button>

      {expanded && (
        <div className="border-t border-white/5 px-4 py-3 space-y-3" data-testid="conflicts-strip-body">
          {/* Severity filter */}
          <div className="flex items-center gap-2">
            {['all', 'HIGH', 'MEDIUM', 'LOW'].map((sev) => (
              <button
                key={sev}
                onClick={() => setSeverity(sev)}
                data-testid={`conflicts-filter-${sev.toLowerCase()}`}
                className={`px-2.5 py-1 rounded-md text-[11px] font-medium transition-colors ${
                  severity === sev
                    ? 'bg-white/10 text-white'
                    : 'bg-white/[0.03] text-zinc-500 hover:text-zinc-300'
                }`}
              >
                {sev === 'all' ? 'All' : sev}
              </button>
            ))}
          </div>

          {/* Rows */}
          {filtered.length === 0 ? (
            <div className="text-xs text-zinc-500 py-4">No conflicts match this filter.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="conflicts-table">
                <thead className="text-zinc-500">
                  <tr className="border-b border-white/5">
                    <th className="text-left font-medium py-2 pr-3">Date</th>
                    <th className="text-left font-medium py-2 pr-3">Outlet</th>
                    <th className="text-left font-medium py-2 pr-3">Primary</th>
                    <th className="text-left font-medium py-2 pr-3">Shadow</th>
                    <th className="text-right font-medium py-2 pr-3">Impact</th>
                    <th className="text-right font-medium py-2">Severity</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c, idx) => (
                    <tr key={`${c.outlet_id}-${c.business_date}-${idx}`} className="border-b border-white/5 last:border-0">
                      <td className="py-2 pr-3 font-mono text-zinc-300">{c.business_date}</td>
                      <td className="py-2 pr-3 text-zinc-300">{c.outlet_name}</td>
                      <td className="py-2 pr-3 text-zinc-400">
                        <span className="font-mono text-[11px]">{c.primary_source_type || '—'}</span>
                        <span className="text-zinc-600 ml-1">({c.primary_row_count})</span>
                      </td>
                      <td className="py-2 pr-3 text-zinc-400">
                        <span className="font-mono text-[11px]">{c.shadow_source_types.join(', ')}</span>
                        <span className="text-zinc-600 ml-1">({c.shadow_row_count})</span>
                      </td>
                      <td className="py-2 pr-3 text-right font-mono text-zinc-300">
                        {c.impact_pct !== null && c.impact_pct !== undefined ? `${c.impact_pct.toFixed(1)}%` : '—'}
                      </td>
                      <td className="py-2 text-right">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md border ${SEVERITY_PILL[c.severity] || SEVERITY_PILL.LOW}`}>
                          {c.severity}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SourceConfidencePage = () => {
  const location = useLocation();
  const initialSeverity = useMemo(() => {
    const p = new URLSearchParams(location.search);
    const sev = (p.get('severity') || '').toUpperCase();
    return ['HIGH', 'MEDIUM', 'LOW'].includes(sev) ? sev : 'all';
  }, [location.search]);

  const [view, setView] = useState('overview'); // overview | detail | recommendations | alerts
  const [selectedOutlet, setSelectedOutlet] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [recommendations, setRecommendations] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [alertCount, setAlertCount] = useState(0);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, alertRes] = await Promise.all([
        api.get('/source-confidence/overview'),
        api.get('/source-confidence/alerts/summary'),
      ]);
      setOutlets(overviewRes.data.outlets || []);
      setAlertCount(alertRes.data.total_unacknowledged || 0);
    } catch (err) {
      toast.error('Failed to load confidence overview');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecommendations = useCallback(async () => {
    try {
      const res = await api.get('/source-confidence/recommendations');
      setRecommendations(res.data.recommendations || {});
    } catch (err) {
      toast.error('Failed to load recommendations');
    }
  }, []);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const handleOutletClick = (outletId) => {
    setSelectedOutlet(outletId);
    setView('detail');
  };

  const handleBack = () => {
    setView('overview');
    setSelectedOutlet(null);
    fetchOverview();
  };

  const handleShowRecommendations = async () => {
    await fetchRecommendations();
    setView('recommendations');
  };

  // Filter logic
  const filtered = filter === 'all' ? outlets : outlets.filter(o => o.readiness_state === filter);

  // State counts
  const counts = {};
  for (const o of outlets) {
    counts[o.readiness_state] = (counts[o.readiness_state] || 0) + 1;
  }

  if (view === 'detail' && selectedOutlet) {
    return (
      <div data-testid="confidence-page">
        <OutletDetailView outletId={selectedOutlet} onBack={handleBack} />
      </div>
    );
  }

  if (view === 'alerts') {
    return (
      <div data-testid="confidence-page">
        <AlertsPanel onBack={handleBack} />
      </div>
    );
  }

  if (view === 'overrides') {
    return (
      <div data-testid="confidence-page">
        <DayOverridesPanel outlets={outlets} onBack={handleBack} />
      </div>
    );
  }

  if (view === 'recommendations') {
    return (
      <div className="space-y-6" data-testid="confidence-page">
        <div className="flex items-center gap-4">
          <button onClick={handleBack} className="p-2 rounded-md hover:bg-white/5 text-zinc-400 hover:text-white transition-colors" data-testid="back-from-recs-btn">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h2 className="text-lg font-medium font-['Outfit']">Cutover Recommendations</h2>
            <p className="text-xs text-zinc-500 mt-0.5">Outlets grouped by recommended action</p>
          </div>
        </div>
        <RecommendationsView recommendations={recommendations} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="confidence-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck size={22} weight="duotone" className="text-cyan-400" />
            <h2 className="text-lg font-medium font-['Outfit']">Source Confidence</h2>
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            Multi-dimensional readiness assessment for POS cutover
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchOverview} className="p-2 rounded-md hover:bg-white/5 text-zinc-400 hover:text-white transition-colors" data-testid="refresh-btn">
            <ArrowClockwise size={18} />
          </button>
          <button onClick={() => setView('alerts')}
            className="relative px-3 py-1.5 rounded-md text-xs font-medium text-zinc-400 bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
            data-testid="show-alerts-btn"
          >
            <Bell size={14} className="inline mr-1.5" />
            Alerts
            {alertCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-[9px] font-bold bg-red-500 text-white rounded-full">{alertCount > 9 ? '9+' : alertCount}</span>
            )}
          </button>
          <button onClick={() => setView('overrides')}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-amber-300 bg-amber-500/10 border border-amber-500/20 hover:bg-amber-500/15 transition-colors"
            data-testid="show-overrides-btn"
          >
            Day Overrides
          </button>
          <button onClick={handleShowRecommendations}
            className="px-3 py-1.5 rounded-md text-xs font-medium text-cyan-400 bg-cyan-500/10 border border-cyan-500/20 hover:bg-cyan-500/15 transition-colors"
            data-testid="show-recommendations-btn"
          >
            Cutover Recommendations
          </button>
        </div>
      </div>

      {/* Source conflicts (read-only projection of source_status) */}
      <ConflictsStrip initialSeverity={initialSeverity} />

      {/* State filter pills */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setFilter('all')}
          className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            filter === 'all' ? 'bg-white/10 text-white' : 'bg-white/[0.03] text-zinc-500 hover:text-zinc-300'
          }`} data-testid="filter-all">
          All <span className="ml-1 font-mono">{outlets.length}</span>
        </button>
        {Object.entries(READINESS_CONFIG).map(([state, cfg]) => {
          const count = counts[state] || 0;
          if (count === 0) return null;
          return (
            <button key={state} onClick={() => setFilter(state)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                filter === state ? `${cfg.bg} ${cfg.text} border ${cfg.border}` : `bg-white/[0.03] text-zinc-500 hover:text-zinc-300`
              }`} data-testid={`filter-${state}`}>
              {cfg.label} <span className="ml-1 font-mono">{count}</span>
            </button>
          );
        })}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="text-zinc-400 text-sm">Loading confidence data...</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="surface-base rounded-lg p-16 text-center">
          <ShieldCheck size={40} weight="duotone" className="mx-auto text-zinc-600 mb-3" />
          <p className="text-zinc-400 text-sm">
            {filter === 'all' ? 'No outlets found with data.' : `No outlets in ${READINESS_CONFIG[filter]?.label || filter} state.`}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(outlet => (
            <OutletCard key={outlet.outlet_id} outlet={outlet} onClick={() => handleOutletClick(outlet.outlet_id)} />
          ))}
        </div>
      )}
    </div>
  );
};

export default SourceConfidencePage;
